"use client";
import FAQDisplay from '@/components/FAQDisplay';
import SubscribeUsForm from '@/components/layout/SubscribeUsForm';
import AboutAuthor from '@/components/ui/AboutAuthor';
import Button from '@/components/ui/Button';
import { extractTextFromHTML, fetchBlogWithViews } from '@/lib/utils/blog-content-reader';
import { ArrowLeft, Calendar, Tag, Volume2, VolumeX } from 'lucide-react';
import { FcGoogle } from "react-icons/fc";
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

interface Blog {
  _id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featuredImage?: string;
  author: string;
  tags: string[];
  publishDate: string;
  formattedDate: string;
  readTime: number;
  views: number;
  createdAt: string;
  updatedAt: string;
}



export function BlogDetailContent({
  slug,
  initialBlog
}: {
  slug: string;
  initialBlog?: Blog;
}) {

  // ✅ IMPORTANT: initialize from server data
  const [blog, setBlog] = useState<Blog | null>(initialBlog || null);
  const [loading, setLoading] = useState(!initialBlog);
  const [error, setError] = useState<string | null>(null);

  const [recentPosts, setRecentPosts] = useState<Blog[]>([]);
  const [recentPostsLoading, setRecentPostsLoading] = useState(true);

  const viewIncrementedRef = useRef(false);
  const [isReading, setIsReading] = useState(false);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  const fetchBlog = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // CRITICAL: Check for case mismatch before fetching
      // This prevents 404 flash if middleware/page redirect didn't catch it
      try {
        const pathname = `/blogs/${slug}`;
        const validateResponse = await fetch(`/api/paths/validate?path=${encodeURIComponent(pathname)}`, {
          cache: 'no-store'
        });

        if (validateResponse.ok) {
          const validateData = await validateResponse.json();

          if (validateData.exists && validateData.needsRedirect && validateData.correctPath) {
            // Redirect immediately to correct case - no 404 shown
            window.location.replace(validateData.correctPath + window.location.search + window.location.hash);
            return; // Exit early - redirect is happening
          }
        }
      } catch (validateErr) {
        // If validation fails, continue with normal fetch
        console.error('Path validation error:', validateErr);
      }

      // Use utility function for fetching blog with view tracking
      const data = await fetchBlogWithViews(slug, '/api/blogs', {
        viewIncrementedRef,
        skipIncrement: false
      });

      if (data && data.blog) {
        setBlog(data.blog as unknown as Blog);
        viewIncrementedRef.current = true;
      } else {
        // Before showing 404, check one more time for case mismatch
        try {
          const pathname = `/blogs/${slug}`;
          const validateResponse = await fetch(`/api/paths/validate?path=${encodeURIComponent(pathname)}`, {
            cache: 'no-store'
          });

          if (validateResponse.ok) {
            const validateData = await validateResponse.json();

            if (validateData.exists && validateData.needsRedirect && validateData.correctPath) {
              // Redirect immediately to correct case - no 404 shown
              window.location.replace(validateData.correctPath + window.location.search + window.location.hash);
              return; // Exit early - redirect is happening
            }
          }
        } catch (validateErr) {
          // If validation fails, show error
          console.error('Final path validation error:', validateErr);
        }

        throw new Error('Blog not found');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err instanceof Error ? err.message : 'Failed to load blog');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchRecentPosts = useCallback(async () => {
    try {
      setRecentPostsLoading(true);
      const response = await fetch('/api/blogs?limit=3&exclude=' + encodeURIComponent(slug));

      if (!response.ok) {
        throw new Error('Failed to fetch recent posts');
      }

      const data = await response.json();
      if (data.blogs && Array.isArray(data.blogs)) {
        setRecentPosts(data.blogs);
      }
    } catch (err) {
      console.error('Error fetching recent posts:', err);
      // Don't show error for recent posts fetch failure
    } finally {
      setRecentPostsLoading(false);
    }
  }, [slug]);



  useEffect(() => {
    if (!slug) return;

    void fetchBlog();
    void fetchRecentPosts();
  }, [slug, fetchBlog, fetchRecentPosts]);

  // CRITICAL: Before showing error, check one final time for case mismatch
  // This must be before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    if (error) {
      const checkCaseMismatch = async () => {
        try {
          const pathname = `/blogs/${slug}`;
          const validateResponse = await fetch(`/api/paths/validate?path=${encodeURIComponent(pathname)}`, {
            cache: 'no-store'
          });

          if (validateResponse.ok) {
            const validateData = await validateResponse.json();

            if (validateData.exists && validateData.needsRedirect && validateData.correctPath) {
              // Redirect immediately to correct case - no 404 shown
              window.location.replace(validateData.correctPath + window.location.search + window.location.hash);
            }
          }
        } catch (validateErr) {
          // If validation fails, allow error to show
          console.error('Error page validation error:', validateErr);
        }
      };

      checkCaseMismatch();
    }
  }, [error, slug]);

  const shareBlog = async () => {
    if (!blog) return;

    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: url,
        });
      } catch {
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link');
    });
  };

  // Read aloud functionality
  const toggleReadAloud = useCallback(() => {
    if (!blog) return;

    // Check if browser supports speech synthesis
    if (!('speechSynthesis' in window)) {
      alert('Your browser does not support text-to-speech. Please use a modern browser like Chrome, Edge, or Safari.');
      return;
    }

    if (isReading) {
      // Stop reading - cancel all pending utterances
      try {
        // Clear any pending timeout
        if (speechTimeoutRef.current) {
          clearTimeout(speechTimeoutRef.current);
          speechTimeoutRef.current = null;
        }
        window.speechSynthesis.cancel();
        // Clear the reference immediately
        speechSynthesisRef.current = null;
        setIsReading(false);
      } catch (error) {
        console.error('Error stopping speech:', error);
        setIsReading(false);
        speechSynthesisRef.current = null;
      }
    } else {
      // Start reading - must be called directly from user interaction
      try {
        // First, ensure any previous speech is fully cancelled
        window.speechSynthesis.cancel();

        // Clear any existing timeout
        if (speechTimeoutRef.current) {
          clearTimeout(speechTimeoutRef.current);
          speechTimeoutRef.current = null;
        }

        // Extract plain text from HTML content
        const plainText = extractTextFromHTML(blog.content || '');

        // Combine title, excerpt, and content for reading
        const textToRead = `${blog.title}. ${blog.excerpt || ''} ${plainText}`.trim();

        if (!textToRead || textToRead.length === 0) {
          alert('No content available to read.');
          return;
        }

        // Create speech synthesis utterance
        const utterance = new SpeechSynthesisUtterance(textToRead);

        // Configure speech settings
        utterance.rate = 1.0; // Normal speed
        utterance.pitch = 1.0; // Normal pitch
        utterance.volume = 1.0; // Full volume
        utterance.lang = 'en-US'; // English

        // Try to get a better voice if available
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          // Prefer English voices
          const englishVoice = voices.find(voice =>
            voice.lang.startsWith('en') && voice.localService
          ) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];

          if (englishVoice) {
            utterance.voice = englishVoice;
          }
        }

        // Set up event handlers
        utterance.onstart = () => {
          setIsReading(true);
        };

        utterance.onend = () => {
          setIsReading(false);
          speechSynthesisRef.current = null;
        };

        utterance.onerror = (event) => {
          console.error('Speech synthesis error:', event);
          console.error('Error details:', {
            error: event.error,
            type: event.type,
            charIndex: event.charIndex,
            utterance: event.utterance?.text?.substring(0, 50)
          });
          setIsReading(false);
          speechSynthesisRef.current = null;
          // Only show error alert for actual errors, not user cancellations
          if (event.error !== 'interrupted' && event.error !== 'canceled') {
            alert(`An error occurred while reading: ${event.error}. Please try again.`);
          }
        };

        // Store reference and start speaking immediately (must be from user interaction)
        speechSynthesisRef.current = utterance;

        // Ensure voices are loaded (some browsers need this)
        if (voices.length === 0) {
          // Wait for voices to load - use a one-time handler
          const voiceHandler = () => {
            const updatedVoices = window.speechSynthesis.getVoices();
            if (updatedVoices.length > 0) {
              const englishVoice = updatedVoices.find(voice =>
                voice.lang.startsWith('en') && voice.localService
              ) || updatedVoices.find(voice => voice.lang.startsWith('en')) || updatedVoices[0];

              if (englishVoice) {
                utterance.voice = englishVoice;
              }
            }
            // Remove the handler after use
            window.speechSynthesis.onvoiceschanged = null;
            window.speechSynthesis.speak(utterance);
            setIsReading(true);
          };
          window.speechSynthesis.onvoiceschanged = voiceHandler;
          // Also try to trigger voice loading
          window.speechSynthesis.getVoices();
        } else {
          // Voices already loaded, speak immediately
          window.speechSynthesis.speak(utterance);
          setIsReading(true);
        }
      } catch (error) {
        console.error('Error starting text-to-speech:', error);
        setIsReading(false);
        speechSynthesisRef.current = null;
        alert('Failed to start reading. Please try again.');
      }
    }
  }, [blog, isReading]);

  // Preload voices on mount (some browsers need this)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      // Trigger voice loading
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) {
        // Some browsers need this to load voices
        window.speechSynthesis.getVoices();
        // Also listen for voices to be loaded
        window.speechSynthesis.onvoiceschanged = () => {
          window.speechSynthesis.getVoices();
        };
      }
    }
  }, []);

  // Cleanup speech synthesis on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeout
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }
      // Cancel any ongoing speech
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 mt-18">
        {/* Header Skeleton */}
        <div className=" sticky top-0 z-10">
          <div className="custom-container mx-auto  py-4">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="custom-container mx-auto py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 ">
            {/* Main Article Skeleton */}
            <div className="lg:col-span-2 space-y-8">
              {/* Hero Section Skeleton */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="animate-pulse">
                  <div className="h-64 md:h-96 bg-gray-200"></div>
                  <div className="p-6 md:p-8 lg:p-10">
                    <div className="flex flex-wrap gap-4 mb-6">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                      <div className="h-4 bg-gray-200 rounded w-18"></div>
                    </div>
                    <div className="h-12 bg-gray-200 rounded mb-6"></div>
                    <div className="flex gap-2 mb-6">
                      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                      <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                    </div>
                    <div className="h-20 bg-gray-200 rounded mb-6"></div>
                    <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
                  </div>
                </div>
              </div>

              {/* Content Skeleton */}
              <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 lg:p-10">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>

              {/* Footer Skeleton */}
              <div className="bg-white rounded-lg shadow-sm p-6 lg:p-8">
                <div className="animate-pulse flex justify-between items-center">
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                  <div className="flex gap-3">
                    <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
                    <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar Skeleton */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-white rounded-lg shadow-sm p-6 lg:p-8">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-32 mb-6"></div>
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex gap-3 pb-4 border-b border-gray-100 last:border-b-0">
                        <div className="w-15 h-15 bg-gray-200 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center relative overflow-hidden">
        <style jsx global>{`
        #global-contact-form {
          display: none !important;
        }
      `}</style>

        {/* Background decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Diagonal lines */}
          <div className="w-full origin-center transform-gpu absolute -left-[40%] top-0 opacity-10">
            <div className="h-[1px] bg-gray-300 rotate-[296.7deg] w-[200%]" />
          </div>
          <div className="w-full origin-center transform-gpu absolute -right-[40%] top-0 opacity-10">
            <div className="h-[1px] bg-gray-300 rotate-[296.8deg] w-[200%]" />
          </div>

          {/* Decorative shapes */}
          <div className="absolute top-20 left-10 opacity-20">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="35" stroke="#272D57" strokeWidth="2" fill="none" />
              <circle cx="40" cy="40" r="25" stroke="#D53333" strokeWidth="1" fill="none" />
            </svg>
          </div>

          <div className="absolute bottom-20 right-10 opacity-20">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="10" y="10" width="40" height="40" stroke="#272D57" strokeWidth="2" fill="none" transform="rotate(45 30 30)" />
              <rect x="15" y="15" width="30" height="30" stroke="#D53333" strokeWidth="1" fill="none" transform="rotate(45 30 30)" />
            </svg>
          </div>

          <div className="absolute top-1/2 right-20 opacity-20">
            <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 50 L50 20 L80 50 L50 80 Z" stroke="#272D57" strokeWidth="2" fill="none" />
              <path d="M30 50 L50 30 L70 50 L50 70 Z" stroke="#D53333" strokeWidth="1" fill="none" />
            </svg>
          </div>
        </div>

        {/* Main Content */}
        <div className="custom-container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            {/* Book/Newspaper Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto h-24 w-24 text-[#272d57]"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.6}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2 4h8a2 2 0 012 2v14a2 2 0 00-2-2H2V4zM22 4h-8a2 2 0 00-2 2v14a2 2 0 012-2h8V4z"
              />
            </svg>


            {/* Title */}
            <h1 className="text-4xl sm:text-5xl font-bold text-[#272d57] mb-4">
              No Blog Posts Found
            </h1>

            {/* Subtitle */}
            <p className="text-lg text-gray-600 mb-8">
              We couldn’t find any articles in this category or matching your search.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">



              <Link href="/blogs">
                <Button size="lg" variant="primary">
                  View All Blogs
                </Button>
              </Link>

            </div>
          </div>
        </div>

        {/* Bottom fade gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 to-transparent opacity-50" />
      </div>

    );
  }

  if (!blog) {
    return null;
  }

  return (
    <div className="min-h-screen  sm:my-18 my-10 ">
      {/* Header */}
      <div className="  sticky top-0 z-10">
        <div className="custom-container mx-auto py-4">
          <Link
            href="/blogs"
            className="inline-flex cursor-pointer items-center text-gray-600 hover:text-gray-800 transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4 " />
            Back to Blogs
          </Link>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 custom-container mx-auto min-h-screen">
        {/* Main Article */}
        <article className="lg:col-span-2 h-fit">
          {/* Hero Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
            {blog.featuredImage && blog.featuredImage.trim() !== '' && (
              <div className="relative h-64 md:h-96">
                <Image
                  src={blog.featuredImage}
                  alt={blog.title}
                  fill
                  className="object-cover"
                  priority
                  onError={(e) => {
                    // Hide the image container if image fails to load
                    const container = e.currentTarget.parentElement;
                    if (container) {
                      container.style.display = 'none';
                    }
                  }}
                />
              </div>
            )}

            <div className="p-6 md:p-8 lg:p-10">
              {/* Meta Information */}
              <div className="flex flex-wrap justify-between items-center gap-4 text-sm text-gray-600 mb-5 border-b border-[#ececec] pb-2 font-medium">
                <div className="flex flex-wrap items-center text-sm text-gray-600 font-medium">
                  {/* Meta items with separators */}
                  <div className="flex items-center gap-4 md:gap-4 divide-x divide-[#ececec]">

                    {/* Date */}
                    <div className="flex items-center justify-center gap-2 px-2">
                      <Calendar className="h-4 w-4   text-gray-500" />
                      <span>{blog.formattedDate}</span>
                    </div>

                    {/* Source preferences */}
                    <div className="flex items-center gap-2 justify-center px-2">
                      <a
                        href="https://www.google.com/preferences/source?q=makemybrandlabs.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sparkle-border relative inline-flex flex-col nowrap items-start hover:scale-105 bg-red-100 -skew-x-12 px-3 py-1.5 text-left text-sm bg-gray-50 hover:bg-gray-100 transition-colors no-underline text-inherit overflow-hidden"
                        title="Your Trusted Source for Insights"
                      >
                        <span className="text-xs text-gray-600 skew-x-12 flex items-center gap-2">
                          <FcGoogle className="h-4 w-4 text-red-500" /> Your Trusted Source for Insights

                        </span>
                      </a>
                    </div>
                  </div>
                </div>


                {/* Tags */}
                {blog.tags.length > 0 && (
                  <div className="flex flex-wrap gap-3 ">
                    {blog.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-semibold bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl  font-semibold text-[#1b1b1b] mb-4 leading-tight tracking-tight">
                {blog.title}
              </h1>


              {/* Excerpt */}
              {blog.excerpt && (
                <p className="text-[1.03rem]  text-[#1b1b1b] leading-relaxed mb-8   leading-snug">
                  {blog.excerpt}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={toggleReadAloud}
                  variant={isReading ? "secondary" : "primary"}
                  size="lg"
                  className="flex !flex-nowrap  "
                >
                  {isReading ? (
                    <>
                      <VolumeX className="h-4 w-4" />
                      Stop Reading
                    </>
                  ) : (
                    <>
                      <Volume2 className="h-4 w-4" />
                      Read Aloud
                    </>
                  )}
                </Button>
                <Button
                  onClick={shareBlog}
                  variant="secondary"
                  size="md"
                >
                  Share Article
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 lg:p-10 mb-8">
            <div
              className="blog-detail-content prose prose-lg prose-gray max-w-none
                           prose-headings:font-bold prose-headings:text-gray-900
                           prose-h1:text-2xl prose-h1:lg:text-3xl prose-h1:mb-6
                           prose-h2:text-xl prose-h2:lg:text-2xl prose-h2:mb-4
                           prose-h3:text-lg prose-h3:lg:text-xl prose-h3:mb-3
                           prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                           prose-li:text-gray-700 prose-li:leading-relaxed
                           prose-strong:font-semibold prose-strong:text-gray-900
                           prose-a:text-blue-600 prose-a:hover:text-blue-800 prose-a:transition-colors"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>

          {/* <FAQDisplay
            blogSlug={blog.slug}
            title="Frequently Asked Questions"
          /> */}

          <FAQDisplay blogSlug={blog.slug} title="Frequently Asked Questions" />
          {/* About Author & Footer Combined */}
          {blog.author && (
            <AboutAuthor
              authorName={blog.author}
              formattedDate={blog.formattedDate}
              footerActions={
                <>
                  <Button
                    href="/blogs"
                    variant="secondary"
                    size="md"
                  >
                    View All Blogs
                  </Button>
                  <Button
                    onClick={toggleReadAloud}
                    variant={isReading ? "secondary" : "primary"}
                    size="md"
                    className="flex flex-row items-center nowrap gap-2 !leading-none"
                  >
                    {isReading ? (
                      <span className="text-sm font-medium">Stop Reading</span>
                    ) : (
                      <span className="text-sm font-medium">Read Aloud</span>
                    )}
                  </Button>
                  <Button
                    onClick={shareBlog}
                    variant="primary"
                    size="md"
                  >
                    Share Article
                  </Button>
                </>
              }
            />
          )}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-1 space-y-8 sticky top-24 self-start">
          {/* Recent Posts */}
          <div className="bg-white rounded-lg shadow-sm p-6 lg:p-8 ">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Tag className="h-5 w-5 mr-3 text-blue-600" />
              Recent Posts
            </h3>
            {recentPostsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex gap-3 pb-4 border-b border-gray-100 last:border-b-0">
                      <div className="w-15 h-15 bg-gray-200 rounded-lg flex-shrink-0"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentPosts.length > 0 ? (
              <div className="space-y-4">
                {recentPosts.map((post) => (
                  <div key={post._id} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                    <Link href={`/blogs/${post.slug}`} className="block group">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-15 h-15 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center overflow-hidden">
                            {post.featuredImage && post.featuredImage.trim() !== '' ? (
                              <Image
                                src={post.featuredImage}
                                alt={post.title}
                                width={60}
                                height={60}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                onError={(e) => {
                                  // Show fallback when image fails to load
                                  const target = e.currentTarget as HTMLImageElement;
                                  target.style.display = 'none';
                                  const fallback = target.nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <span
                              className={`text-blue-600 text-xs font-semibold ${post.featuredImage && post.featuredImage.trim() !== '' ? 'hidden' : 'flex'}`}
                            >
                              {post.title.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2 leading-tight">
                            {post.title}
                          </h4>
                          <p className="text-xs text-gray-600 flex items-center gap-2">
                            <span>{post.formattedDate}</span>
                            <span>•</span>
                            <span>{post.readTime} min read</span>
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500">No recent posts available</p>
              </div>
            )}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <Link
                href="/blogs"
                className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors font-medium text-sm"
              >
                View All Posts →
              </Link>
            </div>
          </div>
          <SubscribeUsForm />

        </aside>
      </div>
    </div>
  );
}
